/**
 * La cáscara de una pantalla de Finance abierta dentro de otro producto.
 *
 * QUE ES ESTO. Unity 360 muestra pantallas reales de Finance corriendo adentro
 * de un iframe. Esta es la página que se abre en ese iframe: no tiene pantalla
 * propia —ni menú, ni encabezado, ni barra lateral—, solo recibe el boleto,
 * canjea la sesión y pinta la pantalla pedida.
 *
 * EL PROTOCOLO, COMPLETO Y EN UN SOLO LUGAR:
 *
 *   anfitrión → adentro
 *     unity:sesion   { codigo, tema }   el boleto de un solo uso, y los colores
 *     unity:tema     { tema }           el anfitrión cambió de tema
 *
 *   adentro → anfitrión
 *     embed:listo                       montado, listo para recibir el boleto
 *     embed:abierto   { pantalla }      la sesión quedó abierta
 *     embed:alto      { alto }          cuánto mide el contenido
 *     embed:sesion-vencida              hace falta otro boleto
 *     embed:error     { mensaje }       no se pudo abrir, y por qué
 *
 * POR QUE `embed:listo` SALE A CUALQUIER ORIGEN y el resto no. Cuando se monta,
 * esta página todavía no sabe quién la enmarcó, y `postMessage` exige un destino.
 * `embed:listo` no lleva ningún dato, así que mandarlo a `'*'` no revela nada.
 * Desde que llega el primer `unity:sesion`, el origen de ese mensaje queda
 * **fijado** y todo lo demás va solo ahí; los mensajes de cualquier otro origen
 * se ignoran.
 *
 * LO QUE NO SE HACE, y es el defecto que se ve hoy entre Finance y Payroll:
 * **nunca se pinta un login acá adentro**. Si la sesión vence, se le avisa al
 * anfitrión y él pide otro boleto. Ver `utils/sesionEmbebida.js`.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import useAuthStore from '../../store/authStore';
import { registrarAvisoDeSesion, borrarSesionEmbebida } from '../../utils/sesionEmbebida';
import { activarModoSuite } from '../../utils/modoSuite';
import { aplicarTemaEmbebido } from '../../utils/temaEmbebido';
import EstadoResultados from '../contabilidad/EstadoResultados';
import PlanCuentas from '../contabilidad/PlanCuentas';

/** Lo que Finance sabe abrir embebido. La misma lista que valida el servidor. */
const PANTALLAS = {
  'estado-resultados': { componente: EstadoResultados, ruta: '/contabilidad/resultados' },
  'catalogo-cuentas': { componente: PlanCuentas, ruta: '/contabilidad/cuentas' }
};

export default function EmbedShell() {
  const { pantalla } = useParams();
  const [estado, setEstado] = useState('esperando'); // esperando · abriendo · abierto · error
  const [mensaje, setMensaje] = useState('');
  const anfitrion = useRef(null);
  const abriendo = useRef(false);
  const contenido = useRef(null);
  const abrirSesionEmbebida = useAuthStore(s => s.abrirSesionEmbebida);

  const elegida = PANTALLAS[pantalla];

  /** Le habla al anfitrión, solo al origen que se fijó al recibir el boleto. */
  const avisar = useCallback((tipo, datos = {}) => {
    if (!anfitrion.current || !window.parent || window.parent === window) return;
    window.parent.postMessage({ tipo, ...datos }, anfitrion.current);
  }, []);

  // Los tokens, la lista blanca y la validación viven en `utils/temaEmbebido.js`,
  // que es el único lugar que toca el CSS de la página con algo que llegó de
  // afuera. Ver `docs/CONTRATO-DISENO-SUITE.md` en Unity.
  const aplicarTema = useCallback((tema) => aplicarTemaEmbebido(tema), []);

  const canjear = useCallback(async (codigo) => {
    if (abriendo.current) return;
    abriendo.current = true;
    setEstado('abriendo');
    try {
      const base = import.meta.env.VITE_API_URL || '';
      // El código va en el CUERPO de un POST, nunca en la dirección: una URL
      // queda en los registros del servidor, en el historial y en el `Referer`.
      const { data } = await axios.post(`${base}/embed/canjear`, { codigo },
        { headers: { 'Content-Type': 'application/json' } });
      const s = data?.data;
      if (!s?.token) throw new Error('El canje no devolvió sesión');

      abrirSesionEmbebida({
        user: s.user,
        token: s.token,
        emisores: s.emisores,
        tenantSlug: s.tenantSlug,
        emisorId: s.emisorId
      });
      setEstado('abierto');
      avisar('embed:abierto', { pantalla, ruta: s.ruta });
    } catch (e) {
      const dato = e.response?.data;
      const texto = dato?.error || e.message || 'No se pudo abrir la sesión';
      // Un boleto vencido no es un error del que mirar: el anfitrión puede pedir
      // otro y volver a intentar sin que el usuario se entere.
      if (dato?.pedirOtro) {
        setEstado('esperando');
        avisar('embed:sesion-vencida');
      } else {
        setEstado('error');
        setMensaje(texto);
        avisar('embed:error', { mensaje: texto });
      }
    } finally {
      abriendo.current = false;
    }
  }, [avisar, abrirSesionEmbebida, pantalla]);

  // ── Escuchar al anfitrión ─────────────────────────────────────────────────
  useEffect(() => {
    const alLlegar = (evento) => {
      const dato = evento.data;
      if (!dato || typeof dato !== 'object' || typeof dato.tipo !== 'string') return;
      if (!dato.tipo.startsWith('unity:')) return;
      // El origen se fija con el primer mensaje válido y después no cambia: si
      // otra ventana intenta hablarle a este iframe, se ignora.
      if (anfitrion.current && evento.origin !== anfitrion.current) return;
      if (!anfitrion.current) {
        anfitrion.current = evento.origin;
        // ── LA FRONTERA SE ENCIENDE ACA (item 096) ─────────────────────────
        //
        // Desde este momento, CUALQUIER pantalla de Finance a la que se navegue
        // adentro se dibuja sin armazon: ni barra lateral, ni encabezado, ni
        // «Salir». La lista blanca de abajo elige con QUE pantalla se abre; ya
        // no finge ser una frontera, porque nunca lo fue — un clic adentro
        // montaba el producto entero.
        //
        // Se enciende al fijar el origen y no al montar, porque el origen es lo
        // que permite hablarle al anfitrion desde cualquier pantalla, y sin
        // anfitrion confirmado no hay a quien avisarle.
        activarModoSuite(evento.origin);
      }

      if (dato.tipo === 'unity:sesion') {
        aplicarTema(dato.tema);
        if (dato.codigo) canjear(dato.codigo);
        return;
      }
      if (dato.tipo === 'unity:tema') aplicarTema(dato.tema);
    };

    window.addEventListener('message', alLlegar);
    // Se avisa que está listo DESPUES de escuchar, no antes: si el anfitrión
    // contesta al instante, el mensaje no se pierde.
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ tipo: 'embed:listo' }, '*');
    }
    return () => window.removeEventListener('message', alLlegar);
  }, [aplicarTema, canjear]);

  // ── Avisar cuando la sesión venza ─────────────────────────────────────────
  useEffect(() => {
    registrarAvisoDeSesion(() => {
      setEstado('esperando');
      avisar('embed:sesion-vencida');
    });
    // El modo suite NO se apaga aca, y es importante: esta cascara se desmonta
    // precisamente cuando se navega a otra pantalla de adentro, que es el momento
    // en que la frontera hace falta. Apagarla aqui la dejaria sin efecto para
    // todo lo que viene despues — o sea, para el defecto entero del item 096. Se
    // apaga sola al cerrar la pestaña, porque vive en `sessionStorage`.
    return () => { registrarAvisoDeSesion(null); borrarSesionEmbebida(); };
  }, [avisar]);

  // ── Informar cuánto mide ──────────────────────────────────────────────────
  // Un iframe con su propia barra de desplazamiento adentro de otra se ve mal
  // siempre; el anfitrión ajusta el alto con esto.
  useEffect(() => {
    if (estado !== 'abierto' || !contenido.current) return;
    let ultimo = 0;
    const medir = () => {
      const alto = Math.ceil(contenido.current?.scrollHeight || 0);
      if (alto && Math.abs(alto - ultimo) > 8) {
        ultimo = alto;
        avisar('embed:alto', { alto });
      }
    };
    const observador = new ResizeObserver(medir);
    observador.observe(contenido.current);
    medir();
    return () => observador.disconnect();
  }, [estado, avisar]);

  // Y cuando se navegue adentro, esta cascara se desmonta: el que mide pasa a ser
  // el armazon en modo suite. Ver `medirYAvisarElAlto` en utils/modoSuite.


  if (!elegida) {
    return (
      <Aviso titulo="Esa pantalla no se abre embebida"
        detalle={`Finance no tiene "${pantalla}" en su lista de pantallas embebibles.`} />
    );
  }

  if (estado === 'error') {
    return <Aviso titulo="No se pudo abrir" detalle={mensaje} />;
  }

  if (estado !== 'abierto') {
    return (
      <div className="embebido min-h-[200px] flex items-center justify-center p-8">
        <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--embed-texto, #6b7280)' }}>
          <span className="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
          Abriendo la pantalla…
        </div>
      </div>
    );
  }

  const Pantalla = elegida.componente;
  return (
    <div className="embebido" ref={contenido}>
      <Pantalla />
    </div>
  );
}

function Aviso({ titulo, detalle }) {
  return (
    <div className="embebido p-8">
      <div className="max-w-lg mx-auto text-center">
        <h2 className="text-base font-semibold" style={{ color: 'var(--embed-texto, #111827)' }}>{titulo}</h2>
        <p className="mt-2 text-sm text-gray-500">{detalle}</p>
      </div>
    </div>
  );
}
